import { assert } from '@disy/lib/custom-error';

import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import {
  getObjecttypeDataDependenciesByObjecttypeId,
  getWorkbookRepositoryName,
  isRepositoryPublishingProcessEnabled,
  isWorkbookWritable,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

// Do not query objecttypeDataDependencies from the store (e.g. with getObjecttypeDataDependenciesByObjecttypeId)
// inside this method, because we are using it in the enrichment-dependencies-dialog.ts, where we are not updating the
// store until we submit the dialog.
export function isEnrichmentRerunnable(state: RootStateOr<WorkbookState>, enrichmentDto: EnrichmentDto) {
  const enrichmentRerunnableStatus = enrichmentDto.enrichmentRerunnableStatus;
  if (
    enrichmentRerunnableStatus.value === 'is-rerunnable' ||
    // we will catch those errors in the rerun itself, showing an error dialog
    enrichmentRerunnableStatus.value === 'geometry-missing-error'
  ) {
    return true;
  }
  const workbookRepositoryName = getWorkbookRepositoryName(state);
  if (enrichmentRerunnableStatus.value === 'is-rerunnable-but-in-published-repository') {
    const enrichmentRepositoryName = enrichmentDto.repositoryName;
    if (workbookRepositoryName === enrichmentRepositoryName) {
      assert(
        isRepositoryPublishingProcessEnabled(state),
        'If the enrichment is in the same repository as the workbook, ' +
          'and the enrichment is in a published repository, ' +
          'then this state must also be set in the workbook store.',
      );
      // If enrichment and workbook are in the same repository, which has the publishing process enabled,
      // the workbook must be writable. This is not included in the rerunnableState
      // because this state is evaluated independently of a workbook in the backend.
      return isWorkbookWritable(state);
    }
    // - else -
    // The enrichment is not in the same repository as the workbook.
    // We don't support rerunning enrichments in such a case because the rerunned enrichment
    // would not be visible in the workbook.
  }
  return false;
}

export function isEnrichmentOutdatedAndRerunnable(state: RootStateOr<WorkbookState>, enrichmentDto: EnrichmentDto) {
  return (
    getObjecttypeDataDependenciesByObjecttypeId(state, enrichmentDto.objecttypeId)?.dataFreshnessState === 'OUTDATED' &&
    isEnrichmentRerunnable(state, enrichmentDto)
  );
}

export function isEnrichmentDirectlyOutdated(state: RootStateOr<WorkbookState>, enrichmentDto: EnrichmentDto) {
  return (
    getObjecttypeDataDependenciesByObjecttypeId(state, enrichmentDto.objecttypeId)
      ?.isDirectlyOutdatedWithoutTransientOutdatedDependencies ?? false
  );
}
